import { useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "@/api";
import { Video, VideoOff, Loader2, VolumeX } from "lucide-react";

// Reusable broadcaster: captures the camera and streams reliable JPEG frames
// (~6-8 fps, video-only) to the given session token. This avoids the fragile
// MediaRecorder->MSE path that often produced a black viewer screen.
export function LiveBroadcast({ token, disabled = false }) {
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const jpegTimerRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | connecting | live | ended | error
  const [err, setErr] = useState("");
  const supported = typeof window !== "undefined" && !!navigator.mediaDevices && !!navigator.mediaDevices.getUserMedia;

  useEffect(() => () => stop(), []); // eslint-disable-line

  const stop = () => {
    try { jpegTimerRef.current && clearInterval(jpegTimerRef.current); } catch (_) {}
    try { wsRef.current && wsRef.current.close(); } catch (_) {}
    try { streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) {}
    jpegTimerRef.current = null; wsRef.current = null; streamRef.current = null;
  };

  const startJpeg = (stream, ws) => {
    try { ws.send(JSON.stringify({ type: "mode", mode: "jpeg" })); } catch (_) {}
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    jpegTimerRef.current = setInterval(() => {
      const v = videoRef.current;
      if (!v || !v.videoWidth || ws.readyState !== 1) return;
      const w = Math.min(960, v.videoWidth);
      const h = Math.round(w * (v.videoHeight / v.videoWidth));
      canvas.width = w; canvas.height = h;
      ctx.drawImage(v, 0, 0, w, h);
      canvas.toBlob((blob) => { if (blob && ws.readyState === 1) blob.arrayBuffer().then((b) => { try { ws.send(b); } catch (_) {} }); }, "image/jpeg", 0.55);
    }, 130);
    setStatus("live");
  };

  const start = async () => {
    setErr(""); setStatus("connecting");
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
        audio: false,
      });
    } catch (e) { setErr("Geen toegang tot de camera. Sta toegang toe en probeer opnieuw."); setStatus("error"); return; }
    streamRef.current = stream;
    if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); }
    const wsBase = BACKEND_URL.replace(/^https/, "wss").replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/api/ws/live/${token}?role=broadcaster`);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    ws.onopen = () => startJpeg(stream, ws);
    ws.onclose = () => setStatus((s) => (s === "live" || s === "connecting" ? "ended" : s));
    ws.onerror = () => setErr("Verbinding met de meldkamer mislukt.");
  };

  return (
    <div data-testid="live-broadcast">
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl border border-slate-800 bg-black">
        <video ref={videoRef} data-testid="live-broadcast-video" playsInline muted className="h-full w-full object-cover" />
        {status === "live" && (
          <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-xs font-bold text-white">
            <span className="h-2 w-2 animate-pulse rounded-full bg-white" /> LIVE
          </div>
        )}
        {status !== "live" && <div className="absolute inset-0 flex items-center justify-center text-slate-500"><Video className="h-10 w-10" /></div>}
      </div>
      {status === "live" && <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-400"><VolumeX className="h-3.5 w-3.5" /> Er wordt alleen beeld gedeeld (geen geluid).</p>}
      {err && <p className="mt-2 text-sm text-red-400">{err}</p>}
      <div className="mt-3">
        {!supported && <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300">Deze browser ondersteunt camera-delen niet.</div>}
        {(status === "idle" || status === "error" || status === "ended") && supported && (
          <button data-testid="live-broadcast-start" onClick={start} disabled={disabled}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 py-3.5 text-base font-bold text-white hover:bg-blue-500 disabled:opacity-50">
            <Video className="h-5 w-5" /> {status === "ended" || status === "error" ? "Opnieuw delen" : "Camera delen"}
          </button>
        )}
        {status === "connecting" && <div className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-800 py-3.5 text-base font-semibold text-white"><Loader2 className="h-5 w-5 animate-spin" /> Verbinden…</div>}
        {status === "live" && (
          <button data-testid="live-broadcast-stop" onClick={() => { stop(); setStatus("ended"); }}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-red-600 py-3.5 text-base font-bold text-white hover:bg-red-500">
            <VideoOff className="h-5 w-5" /> Stoppen met delen
          </button>
        )}
      </div>
    </div>
  );
}
